import React, { useState, useMemo } from 'react';
import { Settings, ChevronDown, AlertTriangle } from 'lucide-react';
import { Button } from '@atoms/Button';
import { useStatuses } from '@hooks/useStatuses';
import type { ChevronStatusData } from '@hooks/useStatuses';

interface StatusOverrideProps {
  currentStatus: string;
  onStatusChange: (newStatus: string) => Promise<void>;
  isLoading: boolean;
  disabled?: boolean;
  className?: string;
}

// Extended interface for display purposes
interface StatusWithDisplayLabel extends ChevronStatusData {
  displayLabel: string;
}

export const StatusOverride: React.FC<StatusOverrideProps> = ({
  currentStatus,
  onStatusChange,
  isLoading,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { chevronData, isLoading: isStatusLoading } = useStatuses();

  // Memoized calculation of available statuses to prevent unnecessary re-computation
  const availableStatuses: StatusWithDisplayLabel[] = useMemo(() => {
    return chevronData
      .filter(status => status.key !== currentStatus)
      .map(status => ({
        ...status,
        displayLabel: `Move to Step ${status.number}: ${status.label}`
      }));
  }, [chevronData, currentStatus]);

  const handleStatusSelect = (status: ChevronStatusData) => {
    setSelectedStatus(status.key);
    setShowConfirm(true);
    setIsOpen(false);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedStatus) return;
    
    try {
      await onStatusChange(selectedStatus);
      setShowConfirm(false);
      setSelectedStatus(null);
    } catch (error) {
      // Enhanced error logging with context
      // Error context available for debugging if needed
      
      // Error details are handled by the error boundary
      
      setShowConfirm(false);
      setSelectedStatus(null);
      
      // Re-throw with enhanced context for parent component
      const enhancedError = error instanceof Error 
        ? new Error(`Status override failed: ${error.message}. Unable to change from ${currentStatus} to ${selectedStatus}.`)
        : new Error(`Status override failed: Unknown error occurred while changing from ${currentStatus} to ${selectedStatus}.`);
      
      // Preserve original error as cause if supported
      if ('cause' in enhancedError) {
        (enhancedError as any).cause = error;
      }
      
      throw enhancedError;
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setSelectedStatus(null);
  };

  const selectedStatusData = chevronData.find(s => s.key === selectedStatus);

  if (isStatusLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-9 w-32 bg-gray-200 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Status Override Button */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
          className="inline-flex items-center px-3 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-50 transition-colors"
        >
          <Settings className="w-4 h-4 mr-2" />
          Override
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
            <div className="py-1">
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="flex items-center text-orange-700">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  <span className="text-xs font-medium">Status Override</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Bypass workflow rules and set any status
                </p>
              </div>
              
              {availableStatuses.map((status) => (
                <button
                  key={status.key}
                  onClick={() => handleStatusSelect(status)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                    <div>
                      <span className="font-medium">{status.displayLabel}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Click outside to close */}
        {isOpen && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && selectedStatusData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancel} />
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 z-50">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Status Override
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                You are about to bypass the normal workflow and change this invoice status to:
              </p>
              
              <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                    style={{ backgroundColor: selectedStatusData.color }}
                  />
                  <div>
                    <span className="font-medium text-orange-900">
                      {selectedStatusData.label}
                    </span>
                    <span className="text-orange-700 ml-2">
                      (Step {selectedStatusData.number})
                    </span>
                  </div>
                </div>
                {selectedStatusData.description && (
                  <p className="text-sm text-orange-700 mt-2 ml-7">
                    {selectedStatusData.description}
                  </p>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mt-3">
                ⚠️ This action will be logged in the audit trail as a status override.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmStatusChange}
                disabled={isLoading}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {isLoading ? 'Updating...' : 'Confirm Override'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
